/**
 * Secure error handling utilities
 * Prevents sensitive information leakage in production
 */

// Safe error logging that doesn't expose sensitive data in production
export const logError = (error, context = '') => {
  // In development, log full error details
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}]`, error);
  } else {
    // In production, log only generic info with a stable code
    console.error(`[${context}] An error occurred:`, {
      code: error?.code || error?.name || 'unknown',
      timestamp: new Date().toISOString(),
    });
  }
};

// Safe error messages for users (never expose internal details)
export const getUserErrorMessage = (error, translations) => {
  // In development, log the full error for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('Full error object:', error);
    console.log('Error code:', error?.code);
    console.log('Error message:', error?.message);
  }
  
  // Map Firebase auth errors to user-friendly messages
  const errorMessages = {
    'auth/invalid-email': translations?.invalidEmail || 'Invalid email address',
    // Use generic for credential issues to avoid account enumeration
    'auth/invalid-credential': translations?.genericError || 'An unexpected error occurred',
    // Allow weak-password guidance during signup flows
    'auth/weak-password': translations?.weakPassword || translations?.genericError || 'Password is too weak',
    // Use a generic message to avoid email enumeration
    'auth/email-already-in-use': translations?.genericError || 'An error occurred. Please try again later',
    'auth/too-many-requests': translations?.tooManyRequests || 'Too many attempts. Please try again later',
    'auth/user-disabled': translations?.genericError || 'Account disabled',
    'auth/user-not-found': translations?.genericError || 'An unexpected error occurred',
    'auth/wrong-password': translations?.genericError || 'An unexpected error occurred',
    'auth/network-request-failed': translations?.networkError || 'Network error. Please check your connection',
    'auth/web-storage-unsupported': 'Browser storage not available (Safari ITP / Private Mode). Enable cookies/storage.',
    'auth/operation-not-allowed': 'Sign-in method disabled.',
    'auth/app-check/token-error': 'Security token error. Reload the page.',
    'permission-denied': translations?.permissionDenied || 'Permission denied. Please check your security rules.',
    'not-found': translations?.notFound || 'The requested resource was not found',
    'network-request-failed': translations?.networkError || 'Network error. Please check your connection',
    // Add Firestore specific error codes
    'FirebaseError': 'Database permission error. Please try again.',
    'insufficient-permission': 'Insufficient permissions for this operation.',
  };
  
  const errorCode = error?.code || error?.name || 'unknown';
  const errorMessage = errorMessages[errorCode] || translations?.genericError || 'An unexpected error occurred';
  
  // In development, show the actual error code too
  if (process.env.NODE_ENV === 'development') {
    return `${errorMessage} (Code: ${errorCode})`;
  }
  
  return errorMessage;
};

// Validate user permissions before operations
export const validateUserPermission = (currentUserId, requiredUserId, action = 'access') => {
  if (!currentUserId) {
    throw new Error('User not authenticated');
  }
  
  if (!requiredUserId) {
    throw new Error('Invalid resource access');
  }
  
  if (currentUserId !== requiredUserId) {
    logError(`Unauthorized ${action} attempt by ${currentUserId} for ${requiredUserId}`, 'SECURITY');
    throw new Error('Insufficient permissions');
  }
  
  return true;
};
